import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, firstValueFrom, of, tap } from 'rxjs';
import { TokenService } from './token.service';
import { ApiResponse } from '../../shared/models/api.models';
import { AuthResponse } from '../../shared/models/auth.models';
import { environment } from '../../../environments/environment';
import { VenueStateService } from '../services/venue-state.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenService = inject(TokenService);
  private readonly router = inject(Router);
  private readonly venueState = inject(VenueStateService);

  readonly currentUser = this.tokenService.currentUser;

  login(email: string, password: string): Observable<ApiResponse<AuthResponse>> {
    return this.http
      .post<ApiResponse<AuthResponse>>(
        `${environment.apiUrl}/auth/login`,
        { email, password, context: 'panel' },
        { withCredentials: true },
      )
      .pipe(tap((res) => this.handleAuthResponse(res, true)));
  }

  register(email: string, password: string, name: string): Observable<ApiResponse<AuthResponse>> {
    return this.http
      .post<ApiResponse<AuthResponse>>(
        `${environment.apiUrl}/auth/register`,
        { email, password, name },
        { withCredentials: true },
      )
      .pipe(tap((res) => this.handleAuthResponse(res, true)));
  }

  loginWithGoogle(idToken: string): Observable<ApiResponse<AuthResponse>> {
    return this.http
      .post<ApiResponse<AuthResponse>>(
        `${environment.apiUrl}/auth/google`,
        { idToken, deviceInfo: 'TriBar Panel Web', context: 'panel' },
        { withCredentials: true },
      )
      .pipe(tap((res) => this.handleAuthResponse(res, true)));
  }

  loginWithApple(
    idToken: string,
    nonce: string,
    fullName?: string,
  ): Observable<ApiResponse<AuthResponse>> {
    return this.http
      .post<ApiResponse<AuthResponse>>(
        `${environment.apiUrl}/auth/apple`,
        {
          idToken,
          nonce,
          fullName: fullName ?? null,
          deviceInfo: 'TriBar Panel Web',
          context: 'panel',
        },
        { withCredentials: true },
      )
      .pipe(tap((res) => this.handleAuthResponse(res, true)));
  }

  refresh(): Observable<ApiResponse<AuthResponse>> {
    return this.http
      .post<ApiResponse<AuthResponse>>(
        `${environment.apiUrl}/auth/refresh`,
        { deviceInfo: 'TriBar Panel Web' },
        { withCredentials: true },
      )
      .pipe(tap((res) => this.handleAuthResponse(res, false)));
  }

  logout(redirect = true): void {
    this.http
      .post(`${environment.apiUrl}/auth/logout`, {}, { withCredentials: true })
      .subscribe({ error: () => {} });
    this.tokenService.clearTokens();
    this.venueState.reset();
    if (redirect) {
      this.router.navigate(['/auth/login']);
    }
  }

  restoreSession(): Promise<void> {
    if (this.tokenService.isAuthenticated() || !this.tokenService.hasRefreshToken()) {
      return Promise.resolve();
    }
    return firstValueFrom(
      this.refresh().pipe(catchError(() => of(null))),
    ).then(() => undefined);
  }

  isAuthenticated(): boolean {
    return this.tokenService.isAuthenticated();
  }

  /**
   * `isNewPrincipal` distinguishes a real login (login/register/google/apple) from the
   * silent token refresh, which reuses the existing session. Only a new principal must
   * wipe cached venue state — otherwise a second person logging in on the same tab after
   * a first person's session (e.g. shared/kiosk device, back button to /auth/login)
   * inherits the previous venue in memory under their own bearer token.
   */
  private handleAuthResponse(res: ApiResponse<AuthResponse>, isNewPrincipal: boolean): void {
    if (res.success && res.data) {
      this.tokenService.setTokens(res.data.accessToken, res.data.user);
      if (isNewPrincipal) {
        this.venueState.reset();
      }
    }
  }
}
